import { createAppError, createHttp, isAppError, type Http } from '@/shared/api'

import { goauthBase } from './goauth-base'

/**
 * The authenticated half of goauth: everything that needs a bearer token.
 *
 * `token-api.ts` stays separate and interceptor-free — a token call must not
 * trigger the refresh it is part of. This client is the opposite: it carries
 * the token, refreshes on rejection, and normalizes goauth's errors into the
 * one shape the app knows.
 *
 * The admin surface used to need a workaround here: its guards collapsed every
 * rejection into a bodyless 404, expiry included, so nothing ever signalled
 * "refresh me". goauth 1.4.2 answers `401 token expired` there, which the
 * standard flow already handles. A bare 404 now means only what it should —
 * a forged token, a caller who is not an admin, or no such route — and none of
 * those are fixed by refreshing.
 */

/** goauth wraps success in `{ data }`. No `success` field — that is api-service. */
const unwrapData = (payload: unknown): unknown =>
  typeof payload === 'object' && payload !== null && 'data' in payload
    ? (payload as { data: unknown }).data
    : payload

/**
 * goauth returns no error codes — only an English sentence — and one status can
 * carry several meanings: 409 is "already exists", "last admin" and "self
 * demote" alike. Classifying by status alone is therefore not enough here, so
 * this table is the one sanctioned place that reads the prose.
 *
 * It is a lookup for a translation key, never a branch in business logic. An
 * unrecognised sentence keeps the status-derived code and still shows its text,
 * so a reworded message degrades to English rather than to nothing.
 */
const MESSAGE_CODES: Record<string, string> = {
  'already exists': 'GOAUTH_ALREADY_EXISTS',
  'cannot remove the last active admin': 'GOAUTH_LAST_ADMIN',
  'cannot remove your own admin rights': 'GOAUTH_SELF_DEMOTE',
  'reauthentication required': 'GOAUTH_REAUTH_REQUIRED',
  'password is too short': 'GOAUTH_PASSWORD_TOO_SHORT',
  'invalid username or email': 'GOAUTH_INVALID_IDENTIFIER',
  'username or email is required': 'GOAUTH_NO_IDENTIFIER',
  'invalid credentials': 'GOAUTH_INVALID_CREDENTIALS',
  'account is blocked': 'GOAUTH_ACCOUNT_BLOCKED',
  'account is deleted': 'GOAUTH_ACCOUNT_DELETED',
}

const STATUS_CODES: Record<number, string> = {
  400: 'BAD_REQUEST',
  401: 'UNAUTHORIZED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  409: 'CONFLICT',
  429: 'TOO_MANY_REQUESTS',
  503: 'SERVICE_UNAVAILABLE',
}

/**
 * Re-tags a goauth failure with a code the translation layer can resolve.
 * `normalizeError` already lifted the sentence into `message`; all that is
 * missing is the key, because goauth ships none.
 */
function withGoauthCode(error: unknown): unknown {
  if (!isAppError(error) || error.code !== undefined) return error

  const code = MESSAGE_CODES[error.message.toLowerCase()] ?? STATUS_CODES[error.status]
  return code === undefined ? error : createAppError({ ...error, code })
}

const client = createHttp(goauthBase, {
  withAuth: true,
  unwrap: unwrapData,
  // goauth authenticates by bearer alone and sends no
  // `Access-Control-Allow-Credentials`. Asking to send cookies anyway would
  // make the browser throw away every cross-origin answer it gives us.
  credentials: 'omit',
})

/**
 * Tagging happens here rather than at each call site, so every consumer sees a
 * coded error without having to remember to ask for one.
 */
const coded =
  <TArgs extends unknown[], TOut>(method: (...args: TArgs) => Promise<TOut>) =>
  (...args: TArgs): Promise<TOut> =>
    method(...args).catch((error: unknown) => {
      throw withGoauthCode(error)
    })

export const goauthHttp: Http = {
  get: coded(client.get),
  post: coded(client.post),
  put: coded(client.put),
  patch: coded(client.patch),
  delete: coded(client.delete),
  getBlob: coded(client.getBlob),
}
